import { createContext, useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";

import {
  useLoginMutation,
  loginApi,
  useLazyGetMyselfQuery,
} from "../api/loginApi";
import { managementApi } from "../api/managementApi";
import { isTokenExpired } from "../utils/jwt";

const tryParseJSON = (string) => {
  try {
    return JSON.parse(string);
  } catch (e) {
    return null;
  }
};

export const getToken = () => {
  const token = localStorage.getItem("token");
  if (token && !isTokenExpired(token)) return token;

  const linkToken = sessionStorage.getItem("linkToken");
  return linkToken || null;
};

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [token, _setToken] = useState(localStorage.getItem("token"));
  const [linkToken, _setLinkToken] = useState(
    sessionStorage.getItem("linkToken"),
  );
  const [linkTokenLevel, setLinkTokenLevel] = useState(
    sessionStorage.getItem("linkTokenLevel"),
  );
  const [linkAccessData, setLinkAccessData] = useState(
    tryParseJSON(sessionStorage.getItem("linkAccessData")),
  );
  const [user, setUser] = useState(tryParseJSON(localStorage.getItem("user")));
  const [authLoading, setAuthLoading] = useState(true);
  const [loginFunc, { isLoading, error }] = useLoginMutation();

  const [triggerGetMyself] = useLazyGetMyselfQuery();
  const dispatch = useDispatch();

  const setToken = (token) => {
    _setToken(token);
  };

  const login = useCallback(
    async (email, password) => {
      try {
        const data = await loginFunc({ email, password }).unwrap();

        if (data && data.token) {
          const { token } = data;
          const user = email;
          if (!token || !user) throw new Error("Invalid credentials");
          setToken(token);
          setUser(user);
          localStorage.setItem("token", token);
          localStorage.setItem("user", JSON.stringify(user));
          dispatch(managementApi.util.resetApiState());
          dispatch(loginApi.util.invalidateTags(["Link"]));
        }
      } catch (err) {
        throw err;
      } finally {
        // setAuthLoading(false);
      }
    },
    [dispatch, loginFunc],
  );

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = tryParseJSON(localStorage.getItem("user"));
    const linkToken = sessionStorage.getItem("linkToken");
    const linkTokenLevel = sessionStorage.getItem("linkTokenLevel");
    const linkAccessData = tryParseJSON(
      sessionStorage.getItem("linkAccessData"),
    );

    if (token && user) {
      setToken(token);
      setUser(user);
    }

    if (linkToken) {
      _setLinkToken(linkToken);
    }

    if (linkTokenLevel) {
      setLinkTokenLevel(linkTokenLevel);
    }

    if (linkAccessData) {
      setLinkAccessData(linkAccessData);
    }

    setAuthLoading(false);
  }, []);

  const setLinkToken = useCallback(
    ({ token: linkToken, level: linkLevel, accessData: linkAccessData }) => {
      _setLinkToken(linkToken);
      setLinkTokenLevel(linkLevel);
      setLinkAccessData(linkAccessData);

      sessionStorage.setItem("linkToken", linkToken);
      sessionStorage.setItem("linkTokenLevel", linkLevel);
      sessionStorage.setItem("linkAccessData", JSON.stringify(linkAccessData));
    },
    [],
  );

  useEffect(() => {
    if (!linkToken && !token) return;
    if (window.location.pathname === "/") return; // do not get myself on homepage

    triggerGetMyself();
  }, [linkToken, token, triggerGetMyself]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token && isTokenExpired(token)) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      _setToken(null);
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        linkToken,
        setLinkToken,
        linkTokenLevel,
        linkAccessData,
        token,
        error,
        isLoading: authLoading,
        isLoginLoading: isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
